import { radioGroupDirectionOptions } from './../../../../utils/radio-group/radio-group-direction.enum';
import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { sizeSelectOptions } from '../form-builder/defaults/size-select-options';
import { InputSize } from '../../../../utils/shared/input-size/input-size.enum';
import { SelectableOption } from '../../../../utils/form-utils/selectable-option.type';
import { SelectMode, SelectModeOptions } from '../../../../utils/select/select-mode.enum';
import { CrudFormItem } from '../../../../utils/edit-form/crud-form-item.interface';
import { CrudFormSelectItem } from '../../../../utils/edit-form/crud-form-select-item.interface';
import { CrudViewFormItemType } from '../../../../utils/edit-form/crud-view-form-item-type.enum';
import { columnOptions, itemOptions, rowOptions } from './../../../../utils/checkbox-table/checkbox-table-placement.enum';
import { checkboxListDirectionOptions } from './../../../../utils/checkbox-list/checkbox-list-direction-options';
import { checkboxTypeOptions } from './../../../../utils/checkbox/checkbox-type-options';
import { buttonTypeOptions } from './../../../../utils/button/button-type-options';
import { textboxTypeOptions } from './../../../../utils/textbox/textbox-type-options';
import { DateFormat } from 'libs/components/src/lib/utils/date-picker';
import { AlertTypes } from 'libs/components/src/lib/utils/alert';
import { TextComponentConfig } from 'libs/components/src/lib/utils/text';

export type CrudFormCombinedItem = CrudFormItem | CrudFormSelectItem;

@Component({
  selector: 'rappider-form-builder-input',
  templateUrl: './form-builder-input.component.html',
  styleUrls: ['./form-builder-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => FormBuilderInputComponent),
      multi: true
    }
  ]
})
export class FormBuilderInputComponent implements ControlValueAccessor, OnChanges {

  @Input() selectedItem: string;
  @Input() itemToAdd: string;

  @Output() formItemSelected = new EventEmitter<string>();
  @Output() scrollToLastItem = new EventEmitter<boolean>();
  @Output() autoSelectFormItem = new EventEmitter<string>();

  _value: any[];
  typeSelectOptions: SelectableOption[] = Object.entries(CrudViewFormItemType).map(([key, value]) => ({ key: key, value: value }));
  initialCrudFormItem: any = {
    type: undefined,
    title: undefined,
    fieldName: undefined,
    columnWidth: 100,
    fixButtonText: undefined,
    fixFunction: undefined,
    selectMode: 'tags'
  };
  formValidators;
  rowFormColumnsConfig = [
    {
      fieldName: 'key',
      config: {
        placeholder: 'Key',
      },
      typeAndFormat: {
        type: 'string'
      }
    },
    {
      fieldName: 'value',
      config: {
        placeholder: 'Value',
      },
      typeAndFormat: {
        type: 'string'
      }
    }
  ];

  checkboxTableCheckConfig = [
    {
      fieldName: 'row',
      config: {
        placeholder: 'Row',
      },
      typeAndFormat: {
        type: 'string'
      }
    },
    {
      fieldName: 'column',
      config: {
        placeholder: 'Column',
      },
      typeAndFormat: {
        type: 'string'
      }
    },
    {
      fieldName: 'title',
      config: {
        placeholder: 'Title',
      },
      typeAndFormat: {
        type: 'string'
      }
    },
    {
      fieldName: 'check',
      typeAndFormat: {
        type: 'boolean'
      }
    }
  ];

  editedIndex: number | undefined;
  isFirstInitialize = true;
  tempCrudFormItem: any = cloneDeep(this.initialCrudFormItem) || {};

  imageUploadWidth: string;
  imageUploadHeight: string;
  imageUploadButtonText: string;
  imageUploadButtonType: string;
  imageUploadButtonIcon: string;
  imageUploadButtonTypeOptions = [
    {
      key: 'Default',
      value: 'default'
    },
    {
      key: 'Primary',
      value: 'primary'
    },
    {
      key: 'Dashed',
      value: 'dashed'
    },
    {
      key: 'Link',
      value: 'link'
    }
  ];

  editButtonIconConfig = {
    type: 'FONT_AWESOME',
    name: 'fa-light fa-pen-to-square'
  };

  deleteButtonIconConfig = {
    type: 'FONT_AWESOME',
    name: 'fa-sharp fa-solid fa-trash'
  };

  initialSelectFormItem: any = {
    size: InputSize.Default,
    selectMode: undefined,
    options: [],
    invalidConfigText: undefined,
    disabled: false,
    type: CrudViewFormItemType.Select
  };

  initialMonacoEditorFormItem: any = {
    sizeSettings: { minHeight: '300px' },
    borderSettings: { border: 'none' },
    options: {
      theme: 'vs-default',
      language: 'typescript',
      minimap: { autohide: true, enabled: false },
      lineNumbers: { renderType: 0 }
    },
    type: CrudViewFormItemType.MonacoCodeEditor
  };

  /* Element Options for Select */
  inputSize = sizeSelectOptions;
  checkboxTypeOptions = checkboxTypeOptions;
  buttonTypeOptions = buttonTypeOptions;
  textboxTypeOptions = textboxTypeOptions;

  rowTitlePlacement = rowOptions;
  columnTitlePlacement = columnOptions;
  itemPlacement = itemOptions;
  selectMode = SelectModeOptions;
  checkboxListDirectionOptions = checkboxListDirectionOptions;
  dateFormatOptions = Object.entries(DateFormat).map(([key, value]) => ({
    key: key,
    value: value
  }));
  radioGroupDirectionOptions = radioGroupDirectionOptions;

  CrudViewFormItemType = CrudViewFormItemType;

  FormBuilderEmptyInfoAlertConfig = {
    type: AlertTypes.Info,
    description: <TextComponentConfig>{
      text: 'Please select the form element you would like to edit'
    },
    closeable: false,
    showIcon: true
  };


  /* every field name must be unique.We're holding this variable to check if there are any overlapped field name. */
  hasFieldNameError = false;
  /* form submittion state */
  tempValueSubmitted = false;
  /* has error while adding new item */
  hasAddError = false;
  /* if the item is new, its true else ( edits ) it's false */
  isAddingControl = false;
  lastInput: any[] | null = null;
  lastOutput: any[] | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedItem) {
      if (this.selectedItem) {
        const editedIndex = this.value?.findIndex(val => val.fieldName === this.selectedItem);

        if (editedIndex !== undefined && editedIndex !== -1) {
          this.onEditItemClick(editedIndex);
        }
      }
    }
    if (changes.itemToAdd) {
      if (this.itemToAdd) {
        this.addUniqueItem(this.itemToAdd);
      }
    }
  }

  get value() {
    return this._value;
  }

  set value(value: any[]) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  onChange: any = (value: any[]) => {
    this._value = value;
  };
  onTouched: any = () => { };

  writeValue(value: any[]) {
    if (!value) {
      this._value = [cloneDeep(this.initialCrudFormItem)];
      this.editedIndex = 0;
      this.isFirstInitialize = true;
    } else {
      this._value = value;
      this.editedIndex = undefined;
      this.isFirstInitialize = false;
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  addFormControl() {
    if (!this.editedIndex && this.editedIndex !== 0) {
      this.value = [...this.value, this.initialCrudFormItem];
      this.editedIndex = this.value.length - 1;
      this.hasAddError = false;
      this.isAddingControl = true;
    } else {
      this.hasAddError = true;
    }
  }

  addUniqueItem(addingItem) {
    const nextIndex = this.calculateNextIndex();
    const initialFieldName = addingItem;
    let fieldName = initialFieldName;
    let counter = 0;

    while (this.value.some(item => item.fieldName === fieldName)) {
      counter++;
      fieldName = `${initialFieldName}${counter}`;
    }
    let newObject;
    if (addingItem === 'button') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        text: 'Button',
        buttonType: 'default',
        section: 'Main Section'
      };
    } else if (addingItem === 'monaco-code-editor') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        editorOptions:
        {
          theme: 'vs',
          language: 'typescript',
          lineNumbers: { renderType: 0 },
          minimap: {
            enabled: false,
          },
        },
        sizeSettings: {
          minHeight: '300px'
        },
        section: 'Main Section'
      };
    } else if (addingItem === 'markdown') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        editorOptions:
        {
          theme: 'vs',
          language: 'markdown',
          lineNumbers: { renderType: 0 },
          minimap: {
            enabled: false,
          },
          wordWrap: 'on',
          wrappingStrategy: 'advanced',
        },
        sizeSettings: {
          minHeight: '300px'
        },
        typeInRealTime: false,
        section: 'Main Section'
      };
    } else if (addingItem === 'checkbox-list') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        direction: 'column',
        options: [
          {
            'key': {
              'text': 'Item 1'
            },
            'value': 'item1',
            'checkboxType': 'default',
            'tooltip': 'tooltip',
            'icon': {
              'name': '',
              'additionalIcon': {
                'name': ''
              }
            }
          },
          {
            'key': {
              'text': 'Item 2'
            },
            'value': 'item2',
            'checkboxType': 'default',
            'tooltip': 'tooltip',
            'icon': {
              'name': '',
              'additionalIcon': {
                'name': ''
              }
            }
          },
          {
            'key': {
              'text': 'Item 3'
            },
            'value': 'item3',
            'checkboxType': 'default',
            'tooltip': 'tooltip',
            'icon': {
              'name': '',
              'additionalIcon': {
                'name': ''
              }
            }
          }
        ]
      };
    } else if (addingItem === 'checkbox-table') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        rows: [
          {
            'key': 'One',
            'value': 'one'
          },
          {
            'key': 'Two',
            'value': 'two'
          },
          {
            'key': 'Three',
            'value': 'three'
          },
          {
            'key': 'Four',
            'value': 'four'
          }
        ],
        columns: [{ 'key': 'Five', 'value': 'five' }, { 'key': 'Six', 'value': 'six' }, { 'key': 'Seven', 'value': 'seven' }, { 'key': 'Eight', 'value': 'eight' }],
        check: [{ 'row': 'one', 'column': 'five', 'check': true }, { 'row': 'three', 'column': 'seven', 'check': true, 'title': 'title' }]
      };
    } else if (addingItem === 'inline-row-form') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        columns: [
          {
            'fieldName': 'key',
            'placeholder': 'Key',
            'typeAndFormat': {
              'type': 'string'
            },
            'unique': true,
            'config': {
              'mask': '(000) 000-0000',
              'placeholder': '(000) 000-0000'
            }
          },
          {
            'fieldName': 'value',
            'placeholder': 'Value',
            'typeAndFormat': {
              'type': 'enum'
            },
            'unique': true,
            'config': {
              'placeholder': 'Cell Phone',
              'options': [
                {
                  'key': 'Work phone',
                  'value': 'W'
                },
                {
                  'key': 'Home phone',
                  'value': 'HP'
                },
                {
                  'key': 'Cell phone',
                  'value': 'CP'
                }
              ]
            }
          }
        ],
        orderNumbersVisibility: true,
        orderable: true
      };

    } else if (addingItem === 'radio') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        options: [
          {
            'key': 'Option',
            'value': 'option1'
          },
          {
            'key': 'Option',
            'value': 'option2'
          },
          {
            'key': 'Option',
            'value': 'option3'
          },
          {
            'key': 'Option',
            'value': 'option4'
          }
        ]
      };
    } else if (addingItem === 'radio-group') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        direction: 'horizontal',
        gap: '10px',
        options: [
          {
            'label': 'Option 1',
            'value': 'opt1',
            'icon': {
              'name': 'fa-thin fa-rocket-launch',
              'type': 'FONT_AWESOME'
            },
            'iconSize': '12px'
          },
          {
            'label': 'Option 2',
            'value': 'opt2',
            'icon': {
              'name': 'fa-thin fa-rocket-launch',
              'type': 'FONT_AWESOME'
            },
            'iconSize': '12px'
          },
          {
            'label': 'Option 3',
            'value': 'opt3',
            'icon': {
              'name': 'fa-thin fa-rocket-launch',
              'type': 'FONT_AWESOME'
            },
            'iconSize': '12px'
          },
          {
            'label': 'Option 4',
            'value': 'opt4',
            'icon': {
              'name': 'fa-thin fa-rocket-launch',
              'type': 'FONT_AWESOME'
            },
            'iconSize': '12px'
          }
        ]
      };
    } else if (addingItem === 'select') {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        placeholder: 'placeholder',
        options: [
          {
            'key': 'option',
            'value': 'Option',
          }
        ],
        selectMode: 'tags'
      };
    } else {
      newObject = {
        type: addingItem,
        title: fieldName,
        columnWidth: 100,
        index: nextIndex,
        fieldName: fieldName,
        section: 'Main Section'
      };
    }

    // Ensure section is set for all new items
    if (!newObject.section) {
      newObject.section = 'Main Section';
    }

    this.value = [...this.value, newObject];

    this.formItemSelected.emit(null);
    this.isFirstInitialize = false;
    this.hasFieldNameError = false;
    this.tempValueSubmitted = false;
    this.isAddingControl = false;
    this.onChange(this.value);
    this.scrollToLastItem.emit(true);
    const editedArrayIndex = this.value?.findIndex(val => val.fieldName === fieldName);
    this.onEditItemClick(editedArrayIndex);
    this.autoSelectFormItem.emit(fieldName);
  }

  onSave() {
    this.tempCrudFormItem.validators = this.manipulateValidators(this.formValidators);

    if (!this.isItemValid()) {
      this.handleSaveError();
      return;
    }

    this.updateFormItem();
    this.resetFormState();
    this.onChange(this.value);
  }

  onImageUploadWidthChange(value) {
    this.tempCrudFormItem = {
      ...this.tempCrudFormItem,
      uploadedImage: {
        width: value,
        height: this.tempCrudFormItem.uploadedImage.height,
        fallback: false
      }
    };
  }

  onImageUploadHeightChange(value) {
    this.tempCrudFormItem = {
      ...this.tempCrudFormItem,
      uploadedImage: {
        height: value,
        width: this.tempCrudFormItem.uploadedImage.width,
        fallback: false
      }
    };
  }

  onImageUploadButtonTextChange(value) {
    this.tempCrudFormItem = {
      ...this.tempCrudFormItem,
      uploadButton: {
        text: value,
        type: this.tempCrudFormItem.uploadButton.type,
        icon: this.tempCrudFormItem.uploadButton.icon
      }
    };
  }

  onImageUploadButtonTypeChange(value) {
    this.tempCrudFormItem = {
      ...this.tempCrudFormItem,
      uploadButton: {
        type: value,
        text: this.tempCrudFormItem.uploadButton.text,
        icon: this.tempCrudFormItem.uploadButton.icon
      }
    };
  }

  onImageUploadButtonIconChange(value) {
    this.tempCrudFormItem = {
      ...this.tempCrudFormItem,
      uploadButton: {
        icon: value,
        type: this.tempCrudFormItem.uploadButton.type,
        text: this.tempCrudFormItem.uploadButton.text
      }
    };
  }

  onEditItemClick(index: number) {
    if (this.isAddingControl) {
      this.value = this.value.filter((item, arrayIndex) => arrayIndex !== this.value.length - 1);
    }
    this.tempCrudFormItem = cloneDeep(this.value[index]) || cloneDeep(this.initialCrudFormItem) || {};
    this.checkItemType();
    this.editedIndex = index;
  }

  checkItemType() {
    if (this.tempCrudFormItem.type === 'image-upload') {
      this.imageUploadHeight = this.tempCrudFormItem?.uploadedImage?.height || null;
      this.imageUploadWidth = this.tempCrudFormItem?.uploadedImage?.width || null;
      this.imageUploadButtonText = this.tempCrudFormItem?.uploadButton?.text || null;
      this.imageUploadButtonType = this.tempCrudFormItem?.uploadButton?.type || null;
      this.imageUploadButtonIcon = this.tempCrudFormItem?.uploadButton?.icon || null;
    }
  }

  onEditCancel() {
    if (this.isAddingControl) {
      this.value = this.value.filter((item, index) => index !== this.editedIndex);
      this.isAddingControl = false;
    }
    this.clearTempValue();
  }

  clearTempValue() {
    this.tempCrudFormItem = cloneDeep(this.initialCrudFormItem) || {};
    this.editedIndex = undefined;
  }

  onTypeChange(type: CrudViewFormItemType) {
    if (!this.tempCrudFormItem) {
      this.tempCrudFormItem = cloneDeep(this.initialCrudFormItem) || {};
    }

    if (type === CrudViewFormItemType.Select) {
      this.tempCrudFormItem = {
        ...this.tempCrudFormItem,
        ...cloneDeep(this.initialSelectFormItem)
      };
    } else if (type === CrudViewFormItemType.MonacoCodeEditor) {
      this.tempCrudFormItem = {
        ...this.tempCrudFormItem,
        ...cloneDeep(this.initialMonacoEditorFormItem)
      };
    } else {
      this.tempCrudFormItem.type = type;
      this.deleteAdditionalFields();
    }
  }

  deleteAdditionalFields() {
    delete this.tempCrudFormItem.size;
    delete this.tempCrudFormItem.settings;
    delete this.tempCrudFormItem.options;
    delete this.tempCrudFormItem.disable;
    delete this.tempCrudFormItem.invalidConfigText;
  }

  onDeleteItemClick(deletedItemIndex: number) {
    this.value = this.value.filter((item, index) => index !== deletedItemIndex);
  }

  drop(event: CdkDragDrop<string[]>) {
    const newValue = [...this.value];
    moveItemInArray(newValue, event.previousIndex, event.currentIndex);
    this.updateItemIndexes(newValue);
    this.value = newValue;
    this.onChange(this.value);
  }

  isItemValid() {
    if (!this.tempCrudFormItem) {
      this.tempCrudFormItem = cloneDeep(this.initialCrudFormItem) || {};
    }
    this.tempCrudFormItem.title = this.tempCrudFormItem.title || null;
    return this.tempCrudFormItem.fieldName && this.tempCrudFormItem.type && this.isFieldNameUnique();
  }

  isFieldNameUnique() {
    if (!this.tempCrudFormItem) {
      this.tempCrudFormItem = cloneDeep(this.initialCrudFormItem) || {};
    }
    const hasOverlappedFieldName = this.value.some((item, index) =>
      item.fieldName === this.tempCrudFormItem.fieldName && index !== this.editedIndex);
    return !hasOverlappedFieldName;
  }

  manipulateValidators(validators: any): any {
    return validators?.map(item => {
      if (item?.type?.startsWith('Validators.')) {
        const regex = /Validators\.(\w+)(?:\((.*?)\))?/;
        const match = item.type.match(regex);

        const errorKey = match ? match[1] : item.type;
        let typeWithValue = item.type;

        if (!match[2] && item.value !== null && item.value !== undefined) {
          typeWithValue = `Validators.${errorKey}(${item.value})`;
        } else if (match && match[2]) {
          typeWithValue = `Validators.${errorKey}(${match[2]})`;
        }

        return {
          type: typeWithValue,
          errorKey: errorKey,
          errorMessage: item.errorMessage,
          fixButtonText: item.fixButtonText || this.tempCrudFormItem.fixButtonText,
          fixFunction: item.fixFunction || this.tempCrudFormItem.fixFunction
        };
      } else {
        return {
          type: item?.type,
          value: item?.value,
          errorKey: item?.type,
          errorMessage: item?.errorMessage,
          fixButtonText: item?.fixButtonText || this.tempCrudFormItem?.fixButtonText,
          fixFunction: item?.fixFunction || this.tempCrudFormItem?.fixFunction
        };
      }

    });
  }

  onValidatorsChange(newValidators) {
    this.formValidators = newValidators;
    this.onSave();
  }

  getTransformValidators(validators: any[]): any[] {
    if (this.lastInput === validators) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.lastOutput!;
    }
    this.lastInput = validators;
    this.lastOutput = validators?.map((validator) => {
      if (validator?.type?.startsWith('Validators.')) {
        const typeMatch = validator.type.match(/(Validators\.\w+)\((.+)\)/);

        if (typeMatch) {
          const typeName = typeMatch[1];
          const value = typeMatch[2];

          return {
            type: `${typeName}()`,
            errorMessage: validator.errorMessage,
            value: isNaN(parseFloat(value)) ? value : parseFloat(value),
            fixButtonText: validator.fixButtonText,
            fixFunction: validator.fixFunction
          };
        }

        return {
          type: validator.type,
          errorMessage: validator.errorMessage,
          fixButtonText: validator.fixButtonText,
          fixFunction: validator.fixFunction
        };
      } else {

        return {
          type: validator.type,
          value: validator.value,
          errorMessage: validator.errorMessage,
          fixButtonText: validator.fixButtonText,
          fixFunction: validator.fixFunction
        };
      }
    });

    this.formValidators = this.lastOutput;
    return this.lastOutput;
  }

  /**
   * Calculate the next index for new items
   */
  private calculateNextIndex(): number {
    const hasExistingIndexes = this.value.some(item => item.index !== undefined);

    if (!hasExistingIndexes) {
      return this.value.length;
    }

    const maxIndex = this.value.length > 0
      ? Math.max(...this.value.map(item => item.index || 0))
      : 0;

    return maxIndex + 1000;
  }

  /**
   * Update item indexes to reflect new order
   */
  private updateItemIndexes(items: any[]): void {
    items.forEach((item, index) => {
      items[index] = { ...item, index };
    });
  }

  /**
   * Update form item with preserved index
   */
  private updateFormItem(): void {
    const originalIndex = this.value[this.editedIndex]?.index;
    const preservedIndex = originalIndex !== undefined ? originalIndex : this.tempCrudFormItem.index;

    this.value = this.value.map((item, arrayIndex) =>
      arrayIndex === this.editedIndex
        ? { ...this.tempCrudFormItem, index: preservedIndex }
        : item
    );
  }

  /**
   * Reset form state after successful save
   */
  private resetFormState(): void {
    this.isFirstInitialize = false;
    this.hasFieldNameError = false;
    this.tempValueSubmitted = false;
    this.isAddingControl = false;
  }

  /**
   * Handle save error state
   */
  private handleSaveError(): void {
    if (!this.isFieldNameUnique()) {
      this.hasFieldNameError = true;
    }
    this.tempValueSubmitted = true;
  }
}
